import React, { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Play, Pause } from "lucide-react"; // Optional: Icons ke liye

gsap.registerPlugin(ScrollTrigger);

const CommonHeroSection = ({
  title,
  description,
  imageSrc, // 'imageSrc' ka naam badal kar 'imageSrc' rakha hai
  imageAlt = "Hero Media",
  subtitle = null,
}) => {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Check if source is a video
  const isVideo = imageSrc?.match(/\.(mp4|webm|ogg|mov)$/i);

  useEffect(() => {
    const context = gsap.context(() => {
      // --- GSAP Animations (Same as your original) ---
      const textElements = gsap.utils.toArray(
        sectionRef.current.querySelectorAll(".animated-text-content")
      );

      textElements.forEach((element, index) => {
        gsap.set(element, {
          y: 25,
          opacity: 0,
          clipPath: "inset(100% 0 0% 0)",
        });
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: element.closest("div"),
            start: "top 85%",
            end: "center 60%",
            scrub: 1,
          },
        });
        tl.to(element, {
          y: 0,
          opacity: 1,
          clipPath: "inset(0% 0 0% 0)",
          duration: 1,
          ease: "power2.out",
          delay: index * 0.1,
        });
      });

      const lineElement = sectionRef.current.querySelector(
        ".about-divider-line"
      );
      if (lineElement) {
        gsap.set(lineElement, { scaleX: 0, transformOrigin: "left" });
        gsap.to(lineElement, {
          scaleX: 1,
          scrollTrigger: {
            trigger: lineElement.closest(".about-subtitle"),
            start: "top 85%",
            end: "center 60%",
            scrub: 1,
          },
        });
      }
    }, sectionRef);

    return () => context.revert();
  }, []);

  // Play/Pause Toggle Function
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <div
      ref={sectionRef}
      className="flex flex-col gap-[25px] sm:gap-[37px] lg:gap-[50px] justify-start items-center w-full mt-[40px] md:mt-[80px] 2xl:mt-[132px] px-[32px]"
    >
      <div className="flex flex-col gap-[25px] sm:gap-[37px] lg:gap-[50px] justify-start items-center w-full max-w-[806px] sm:max-w-[1209px] lg:max-w-[1612px]">
        {/* Text Content */}
        <div className="flex flex-col gap-[10px] sm:gap-[15px] lg:gap-[20px] justify-start items-center w-full">
          {subtitle && (
            <div className="about-subtitle flex justify-center items-center gap-[20px] mb-2">
              <span className="animated-text-content text-sm font-semibold uppercase tracking-widest">
                {subtitle}
              </span>
              <div className="about-divider-line h-[2px] w-[50px] bg-black"></div>
            </div>
          )}
          <h1 className="animated-text-content text-[24px] sm:text-[45px] lg:text-[60px] font-bold leading-[37px] sm:leading-[56px] lg:leading-[75px] text-center font-[Sora] w-auto">
            {title}
          </h1>
          <p className="animated-text-content text-[12px] sm:text-[18px] lg:text-[24px] font-normal leading-[19px] sm:leading-[28px] lg:leading-[38px] text-center w-full font-[Sora]">
            {description}
          </p>
        </div>

        {/* Media Container (Image or Video) */}
        <div className="relative w-full bg-neutral-200 rounded-[15px] sm:rounded-[22px] lg:rounded-[30px] h-[343px] sm:h-[514px] lg:h-[686px] overflow-hidden group">
          {isVideo ? (
            <>
              <video
                ref={videoRef}
                src={imageSrc}
                className="w-full h-full object-cover"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                loop
                muted // Video aksar muted autoplay hoti hai browsers mein
                playsInline
              />
              {/* Play/Pause Custom Button Over Video */}
              <button
                onClick={togglePlay}
                className="absolute inset-0 m-auto w-16 h-16 bg-white/30 backdrop-blur-md rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              >
                {isPlaying ? (
                  <Pause size={32} />
                ) : (
                  <Play size={32} className="ml-1" />
                )}
              </button>
            </>
          ) : (
            <img
              src={imageSrc}
              alt={imageAlt}
              className="w-full h-full object-cover"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CommonHeroSection;
